import { MinusIcon, PlusIcon, TrashIcon } from "@/components/icons";
import { Badge, Button, Input } from "@/components/kit";
import { formatLak } from "@/shared/lib/currency";
import {
  CUSTOM_JOB_LINE_TYPE_LABEL,
  CUSTOM_JOB_LINE_TYPE_BADGE_VARIANT,
} from "./job-status";

export type CustomJobFormLine =
  | {
      key: string;
      lineType: "material";
      variantId: string;
      description: string;
      quantity: number;
      unitPrice: string;
    }
  | {
      key: string;
      lineType: "labor" | "other";
      description: string;
      quantity: number;
      unitPrice: string;
    };

const LINE_CHIP_VARIANT = CUSTOM_JOB_LINE_TYPE_BADGE_VARIANT;

type CustomJobFormLinesProps = {
  lines: CustomJobFormLine[];
  onChange: (next: CustomJobFormLine[]) => void;
};

export function CustomJobFormLines({ lines, onChange }: CustomJobFormLinesProps) {
  const updateLine = (key: string, patch: Partial<CustomJobFormLine>) => {
    onChange(
      lines.map((line) =>
        line.key === key ? ({ ...line, ...patch } as CustomJobFormLine) : line,
      ),
    );
  };

  const removeLine = (key: string) => {
    onChange(lines.filter((line) => line.key !== key));
  };

  const bumpQty = (key: string, delta: number) => {
    onChange(
      lines.map((line) =>
        line.key === key
          ? { ...line, quantity: Math.max(1, line.quantity + delta) }
          : line,
      ),
    );
  };

  if (lines.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-2xl border border-secondary/20 border-dashed px-6 py-10 text-center">
        <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີລາຍການ</p>
        <p className="text-muted-foreground text-xs">
          ເພີ່ມວັດຖຸດິບ, ຄ່າແຮງ ຫຼື ລາຍການອື່ນໆ
        </p>
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-3">
      {lines.map((line, index) => {
        const lineTotal = Number(line.unitPrice || 0) * line.quantity;
        return (
          <li
            key={line.key}
            className="animate-order-item-enter overflow-hidden rounded-2xl border border-secondary/20 bg-background/90"
            style={{ animationDelay: `${Math.min(index, 4) * 40}ms` }}
          >
            <div className="flex items-start justify-between gap-2 border-secondary/15 border-b px-3 py-2 sm:px-4">
              <Badge
                variant={LINE_CHIP_VARIANT[line.lineType]}
                className="rounded-full text-[10px]"
              >
                {CUSTOM_JOB_LINE_TYPE_LABEL[line.lineType]}
              </Badge>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="size-8 shrink-0 rounded-full text-muted-foreground hover:text-destructive"
                onClick={() => removeLine(line.key)}
              >
                <TrashIcon className="size-4" />
              </Button>
            </div>

            <div className="p-3 sm:p-4">
              {line.lineType === "material" ? (
                <p className="font-medium text-sm leading-snug">{line.description}</p>
              ) : (
                <Input
                  className="rounded-full"
                  value={line.description}
                  onChange={(e) =>
                    updateLine(line.key, { description: e.target.value })
                  }
                  placeholder="ຊື່ລາຍການ"
                />
              )}

              <div className="mt-3 grid grid-cols-[auto_1fr_auto] items-center gap-3">
                <div className="flex items-center rounded-full border border-secondary/25 bg-muted/30 p-0.5">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-8 rounded-full"
                    onClick={() => bumpQty(line.key, -1)}
                  >
                    <MinusIcon className="size-3.5" />
                  </Button>
                  <span className="min-w-8 text-center font-medium text-sm tabular-nums">
                    {line.quantity}
                  </span>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-8 rounded-full"
                    onClick={() => bumpQty(line.key, 1)}
                  >
                    <PlusIcon className="size-3.5" />
                  </Button>
                </div>

                <Input
                  type="number"
                  min={0}
                  className="rounded-full"
                  value={line.unitPrice}
                  onChange={(e) =>
                    updateLine(line.key, { unitPrice: e.target.value })
                  }
                  placeholder="ລາຄາ/ຫນ່ວຍ"
                />

                <span className="font-display text-secondary text-sm tabular-nums">
                  {formatLak(lineTotal)}
                </span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

type CustomJobFormSummaryBarProps = {
  subtotal: number;
  discountAmount: number;
  total: number;
  isSubmitting: boolean;
  isEdit: boolean;
  onSubmit: () => void;
};

export function CustomJobFormSummaryBar({
  subtotal,
  discountAmount,
  total,
  isSubmitting,
  isEdit,
  onSubmit,
}: CustomJobFormSummaryBarProps) {
  return (
    <div className="fixed inset-x-0 bottom-(--mobile-bottom-nav,0px) z-20 border-secondary/20 border-t bg-background/95 backdrop-blur">
      <div className="mx-auto flex max-w-3xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col">
          <span className="text-muted-foreground text-xs">
            ລາຍການ {formatLak(subtotal)}
            {discountAmount > 0 ? ` − ສ່ວນລົດ ${formatLak(discountAmount)}` : ""}
          </span>
          <span className="font-display text-foreground text-xl">
            ຍອດລວມ:{" "}
            <span className="text-secondary tabular-nums">{formatLak(total)}</span>
          </span>
        </div>
        <Button
          className="w-full rounded-full sm:w-auto sm:min-w-40"
          disabled={isSubmitting}
          isLoading={isSubmitting}
          onClick={onSubmit}
        >
          {isEdit ? "ບັນທຶກ" : "ສ້າງງານ"}
        </Button>
      </div>
    </div>
  );
}
